import styled from '@emotion/styled';
import ArrowForward from '@mui/icons-material/ArrowForward';
import { FunctionComponent } from 'react';
import { Trans } from 'react-i18next';

import useBookingUrl from '../hooks/useBookingUrl';
import useStepperState from '../hooks/useStepperState';
import AppStyles, { Size } from '../styling/AppStyles';
import { spaceBetween } from '../styling/mixins';
import StepBookingCenter, { Center } from './steps/StepBookingCenter';
import StepBookingCountry, { Country } from './steps/StepBookingCountry';
import StepBookingSummary from './steps/StepBookingSummary';
import StepBookNowChoice, { BookNowOption } from './steps/StepBookNowChoice';
import StepCertification, { Certification } from './steps/StepCertification';
import StepCertificationLanguage, { Language } from './steps/StepCertificationLanguage';
import StepCertificationMethod, { Method } from './steps/StepCertificationMethod';
import StepDownload from './steps/StepDownload';
import StepLocation from './steps/StepLocation';
import StepTrainingCompleteChoice from './steps/StepTrainingCompleteChoice';
import StepTrainingLanguage from './steps/StepTrainingLanguage';
import ExinMuiButton from './styled/ExinMuiButton';
import ExinStepper, { Step } from './styled/ExinStepper';
import ExinH2Text from './styled/ExinText';
import TrainerList from './trainers/TrainerList';

function getStepsForStepperState({
  bookNow,
  center,
  certification,
  country,
  language,
  method,
  // trainingComplete,
  trainingLanguage,
  setBookNow,
  setCenter,
  setCertification,
  setCountry,
  setLanguage,
  setLocation,
  setMethod,
  setTrainingComplete,
  setTrainingLanguage,
}: ReturnType<typeof useStepperState>): Array<Step> {
  const steps = [
    {
      // step index = 0
      key: 'certification',
      label: (
        <Trans i18nKey="stepCertificationLabel">
          Which certification would you like to attain?
        </Trans>
      ),
      content: <StepCertification onChange={setCertification} />,
    },
    {
      // step index = 1
      key: 'language',
      label: (
        <Trans i18nKey="stepCertificationLanguageLabel">
          Which language would you like to take your exam in?
        </Trans>
      ),
      content: (
        <StepCertificationLanguage
          certification={certification as Certification}
          selectedLanguage={language}
          setSelectedLanguage={setLanguage}
        />
      ),
    },
    {
      // step index = 2
      key: 'method',
      label: (
        <Trans i18nKey="stepCertificationMethodLabel">
          Choose the certification method which suits you:
        </Trans>
      ),
      content: (
        <StepCertificationMethod
          certification={certification as Certification}
          trainingNeeded={!!certification?.trainingNeeded}
          selectedMethod={method}
          setSelectedMethod={setMethod}
        />
      ),
    },
  ];

  if (method === Method.SELFSTUDY_EXAM) {
    steps.push(
      {
        // step index = 3
        key: 'download',
        label: <Trans i18nKey="selfStudyDownloadsLabel">Your Downloads for Self-Study</Trans>,
        content: (
          <StepDownload
            certification={certification as Certification}
            language={language as Language}
          />
        ),
      },
      {
        // step index = 4
        key: 'bookNow',
        label: <Trans i18nKey="bookExamNowLabel">Would you like to book your exam now?</Trans>,
        content: <StepBookNowChoice onChange={setBookNow} />,
      }
    );
  }
  if (method === Method.EXAM_ONLY && certification?.trainingNeeded) {
    steps.push({
      // step index = 3
      key: 'trainingcomplete',
      label: <Trans i18nKey="trainingCompletedLabel">Did you complete training?</Trans>,
      content: <StepTrainingCompleteChoice onChange={setTrainingComplete} />,
    });
  }

  if (
    method === Method.EXAM_ONLY ||
    (method === Method.SELFSTUDY_EXAM && bookNow === BookNowOption.YES)
  ) {
    steps.push(
      {
        // step index = 4 or 5
        key: 'country',
        label: <Trans i18nKey="bookingCountryLabel">Where will you take your exam?</Trans>,
        content: (
          <StepBookingCountry
            certification={certification as Certification}
            onChange={setCountry}
          />
        ),
      },
      {
        // step index = 5 or 6
        key: 'center',
        label: <Trans i18nKey="bookingCenterLabel">How would you like to take your exam?</Trans>,
        content: (
          <StepBookingCenter center={center} country={country as Country} setCenter={setCenter} />
        ),
      },
      {
        // step index = 6 or 7
        key: 'summary',
        label: <Trans i18nKey="bookingSummaryLabel">Overview and Price</Trans>,
        content: (
          <StepBookingSummary
            center={center as Center}
            certification={certification as Certification}
            country={country as Country}
            language={language as Language}
            method={method}
          />
        ),
      }
    );
  }

  if (
    method === Method.TRAINING_EXAM ||
    method === Method.VIRTUAL_CLASSROOM ||
    method === Method.ELEARNING_EXAM
  ) {
    steps.push({
      // step index = 3
      key: 'training-language',
      label: <Trans i18nKey="trainingLanguageLabel">Training language</Trans>,
      content: (
        <StepTrainingLanguage
          certification={certification as Certification}
          method={method}
          selectedLanguage={trainingLanguage}
          setSelectedLanguage={setTrainingLanguage}
        />
      ),
    });
  }

  if (method === Method.TRAINING_EXAM) {
    steps.push({
      // step index = 4
      key: 'location',
      label: <Trans i18nKey="myLocationLabel">My location</Trans>,
      content: <StepLocation onChange={setLocation} rangeMin={50} rangeMax={250} rangeStep={50} />,
    });
  }

  return steps;
}

const StepperContainer = styled.div`
  ${spaceBetween(Size.large)};
  max-width: 680px;
  margin: 0 auto;
  padding: 20px 0;

  @media screen and (max-width: 680px) {
    margin: 0 10px;
  }
`;

const DownloadNowButtonContainer = styled.div`
  max-width: 680px;
  margin: ${Size.small} auto 0 auto;
  text-align: center;
`;

const TrainerContainer = styled.div`
  background-color: white;
  padding: 10px 0;
`;

const App: FunctionComponent = () => {
  const state = useStepperState();
  const bookingUrl = useBookingUrl(state);

  const { activeStep, method, trainingLanguage, certification, location } = state;
  const showTrainersList =
    activeStep === 4 &&
    // Either the user chose method "Training & Exam" and the location is filled out,
    ((method === Method.TRAINING_EXAM && location) ||
      // Or method is fully online
      method === Method.ELEARNING_EXAM ||
      method === Method.VIRTUAL_CLASSROOM);

  return (
    <AppStyles>
      <StepperContainer>
        <ExinStepper steps={getStepsForStepperState(state)} activeStep={activeStep} />

        {bookingUrl.isVisible && (
          <DownloadNowButtonContainer>
            <ExinMuiButton
              disabled={!bookingUrl.isEnabled}
              href={bookingUrl.isEnabled ? bookingUrl.url : undefined}
              endIcon={<ArrowForward />}
              // @ts-ignore
              rel="noopener"
              // @ts-ignore
              target="_blank"
            >
              <Trans i18nKey="bookExamNowButton">Book exam now</Trans>
            </ExinMuiButton>
          </DownloadNowButtonContainer>
        )}
      </StepperContainer>

      {showTrainersList && (
        <TrainerContainer>
          <ExinH2Text>
            <Trans i18nKey="accreditedTrainingOrganisationsTitle">
              Accredited Training Organizations
            </Trans>
          </ExinH2Text>
          <TrainerList
            location={location}
            method={method}
            language={trainingLanguage as Language}
            certification={certification as Certification}
          />
        </TrainerContainer>
      )}
    </AppStyles>
  );
};

export default App;
